<template>
  <div class="main-wrapper">
    <hr />
    <el-row
      type="flex"
      class="mb-1 align-center"
    >
      <b class="section-subheader">Transcription Upload Preferences</b>
    </el-row>

    <!-- Start show error banner if missing scopes -->
    <template v-if="hasError">
      <div class="p-3 container mt-3 mb-4">
        <i class="el-icon-warning-outline mr-1"></i>
        <span>
          {{ errorMessage }}
        </span>
      </div>
    </template>
    <!-- End show error banner if missing scopes-->

    <!-- Start show toggle and selector if scopes are available -->
    <template v-else>
      <el-row>
        <p class="mt-2">
          When enabled, call transcriptions will <strong>automatically be uploaded</strong> to the HubSpot folder you choose.
          For better organization, a new, separate subfolder will be created inside your chosen folder for every contact, and each subfolder will be named using that contact's unique <strong>Aloware contact ID</strong>.
        </p>
      </el-row>
      <el-row>
        <el-switch
          v-model="transcriptionUploadStatus"
          active-color="#00a344"
        >
        </el-switch>
      </el-row>

      <template v-if="transcriptionUploadStatus">
        <!-- Start Folder Selector -->
        <el-row class="mt-3">
          <el-col :span="24">
            <label class="folder-label">Select HubSpot Folder</label>
            <el-select
              v-model="selectedFolderId"
              class="w-full mt-2"
              filterable
              remote
              reserve-keyword
              remote-show-suffix
              placeholder="Select a folder"
              :loading="loadingFolders"
              :disabled="loadingFolders"
              :remote-method="searchFolders"
            >
              <el-option
                v-for="folder in availableFolders"
                :key="folder.id"
                :label="folder.name + '      ' + folder.path"
                :value="folder.id"
              >
                <span>{{ folder.name }}</span>
                <span style="float: right; color: #8492a6; font-size: 13px">{{ folder.path }}</span>
              </el-option>
            </el-select>
          </el-col>
        </el-row>
        <!-- End Folder Selector -->

        <!-- Overwrite Files Toggle -->
        <el-row style="display: flex; justify-content: space-between; align-items: center; margin-top: 20px;">
          <div style="display: flex; flex-direction: column; justify-content: center; width: 100%;">
            <b>Overwrite Existing Files</b>
            <span>Enable to replace files with the same name and extension in a contact's folder.</span>
          </div>
          <el-switch
            v-model="overwriteEnabled"
            active-color="#00a344"
          >
          </el-switch>
        </el-row>
        <!-- End Overwrite Files Toggle -->
      </template>
    </template>
    <!-- End show toggle and selector if scopes are available -->
    <hr />
  </div>
</template>

<script>
import _ from 'lodash'

export default {
  props: {
    model: {
      type: Object,
      required: true
    },
    defaultFileUploadSettings: {
      type: Object,
      required: false
    }
  },
  data() {
    return {
      availableFolders: [],
      loadingFolders: false,
      hasError: false,
      errorMessage: ''
    }
  },
  computed: {
    transcriptionUploadStatus: {
      get() {
        return this.model.settings.file_upload_settings?.transcription_attachment?.enabled ?? false
      },
      set(value) {
        this.handleTranscriptionUploadStatusChange(value)
      }
    },
    selectedFolderId: {
      get() {
        return this.model.settings.file_upload_settings?.transcription_attachment?.folder_id ?? null
      },
      set(value) {
        this.handleFolderSelection(value)
      }
    },
    overwriteEnabled: {
      get() {
        return this.model.settings.file_upload_settings?.transcription_attachment?.overwrite ?? false
      },
      set(value) {
        this.handleOverwriteChange(value)
      }
    }
  },
  async created() {
    // Ensure file_upload_settings exists
    if (!this.model.settings.file_upload_settings) {
      this.$set(this.model.settings, 'file_upload_settings', {})
    }

    // Migrate old settings shape (if present) into file_upload_settings
    if (
      !this.model.settings.file_upload_settings?.transcription_attachment &&
      this.model.settings.transcription_upload_settings?.transcription_attachment
    ) {
      this.$set(
        this.model.settings.file_upload_settings,
        'transcription_attachment',
        _.cloneDeep(this.model.settings.transcription_upload_settings.transcription_attachment)
      )
      this.$delete(this.model.settings, 'transcription_upload_settings')
    }

    // Initialize transcription_attachment defaults if missing
    if (
      !this.model.settings.file_upload_settings?.transcription_attachment &&
      this.defaultFileUploadSettings?.transcription_attachment
    ) {
      this.$set(
        this.model.settings.file_upload_settings,
        'transcription_attachment',
        _.cloneDeep(this.defaultFileUploadSettings.transcription_attachment)
      )
    }

    // If there's a currently selected folder, add it to the list so it shows up in the dropdown
    const currentFolder = this.model.settings.file_upload_settings?.transcription_attachment
    if (currentFolder?.folder_id && currentFolder?.folder_name && currentFolder?.folder_path) {
      this.availableFolders = [{
        id: currentFolder.folder_id,
        name: currentFolder.folder_name,
        path: currentFolder.folder_path
      }]
    }

    await this.fetchFolders('aloware')
  },
  methods: {
    async fetchFolders(query = '') {
      try {
        this.loadingFolders = true
        const params = query ? { name: query } : {}
        const response = await axios.get('/api/v1/integration/hubspot/folders', { params })
        if (response.data.success) {
          const searchResults = response.data.data

          const currentFolder = this.model.settings.file_upload_settings?.transcription_attachment
          const currentFolderId = currentFolder?.folder_id

          if (currentFolderId && currentFolder.folder_name && currentFolder.folder_path) {
            const selectedFolderInResults = searchResults.find(f => f.id === currentFolderId)
            if (!selectedFolderInResults) {
              this.availableFolders = [
                {
                  id: currentFolder.folder_id,
                  name: currentFolder.folder_name,
                  path: currentFolder.folder_path
                },
                ...searchResults
              ]
            } else {
              this.availableFolders = searchResults
            }
          } else {
            this.availableFolders = searchResults
          }
        }
      } catch (error) {
        if (error.response?.status === 400) {
          this.hasError = true
          this.errorMessage = error.response?.data?.message || 'An error occurred while loading folders'
        } else {
          console.error('Error fetching folders:', error)
          this.hasError = true
          this.errorMessage = 'Failed to load HubSpot folders'
        }
      } finally {
        this.loadingFolders = false
      }
    },
    searchFolders: _.debounce(function(query) {
      if (query) {
        this.fetchFolders(query)
      }
    }, 800),
    handleTranscriptionUploadStatusChange(newVal) {
      if (!this.model.settings.file_upload_settings) {
        this.$set(this.model.settings, 'file_upload_settings', {})
      }
      if (!this.model.settings.file_upload_settings.transcription_attachment) {
        this.$set(this.model.settings.file_upload_settings, 'transcription_attachment', {
          enabled: false,
          folder_id: null,
          folder_name: null,
          folder_path: null,
          overwrite: false
        })
      }

      this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'enabled', newVal)

      if (!newVal) {
        this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'folder_id', null)
        this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'folder_name', null)
        this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'folder_path', null)
        this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'overwrite', false)
      }
    },
    handleFolderSelection(folderId) {
      if (!folderId) {
        if (this.model.settings.file_upload_settings?.transcription_attachment) {
          this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'folder_id', null)
          this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'folder_name', null)
          this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'folder_path', null)
        }
        return
      }

      const selectedFolder = this.availableFolders.find(folder => folder.id === folderId)
      if (!selectedFolder) {
        return
      }

      if (!this.model.settings.file_upload_settings) {
        this.$set(this.model.settings, 'file_upload_settings', {})
      }
      if (!this.model.settings.file_upload_settings.transcription_attachment) {
        this.$set(this.model.settings.file_upload_settings, 'transcription_attachment', {
          enabled: true,
          folder_id: null,
          folder_name: null,
          folder_path: null,
          overwrite: false
        })
      }

      this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'folder_id', selectedFolder.id)
      this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'folder_name', selectedFolder.name)
      this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'folder_path', selectedFolder.path)
    },
    handleOverwriteChange(value) {
      if (!this.model.settings.file_upload_settings) {
        this.$set(this.model.settings, 'file_upload_settings', {})
      }
      if (!this.model.settings.file_upload_settings.transcription_attachment) {
        this.$set(this.model.settings.file_upload_settings, 'transcription_attachment', {
          enabled: false,
          folder_id: null,
          folder_name: null,
          folder_path: null,
          overwrite: false
        })
      }

      this.$set(this.model.settings.file_upload_settings.transcription_attachment, 'overwrite', value)
    }
  }
}
</script>

<style scoped>
.main-wrapper {
  width: 100%;
}

.section-subheader {
  font-size: 16px;
}

.align-center {
  align-items: center;
}

.container {
  border: 1px solid grey;
  border-radius: 5px;
  width: 100%;
  max-width: 100%;
}

.folder-label {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
}

.w-full {
  width: 100%;
}

.mt-2 {
  margin-top: 0.5rem;
}

.mt-3 {
  margin-top: 1rem;
}

.mb-1 {
  margin-bottom: 0.25rem;
}

.mb-4 {
  margin-bottom: 1.5rem;
}

.mr-1 {
  margin-right: 0.25rem;
}

.p-3 {
  padding: 1rem;
}
</style>


